import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import {
    AccountRow,
    BodySmallRegular,
    ChevronRightIcon,
    DownloadIcon,
    Icon,
    XStack,
    YStack,
} from 'smoothie';

import { getDefaultedAccountNickname } from '../../../common/utils/bankingUtils';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import {
    detectedPaychecksAtom,
    existingPaycheckRoutingConfigsAtom,
} from '../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../state/SpacesAtoms';
import {
    type BankAccountDisplayDetail,
    RelationshipStatus,
} from '../../types/BankAccount';
import { type PaycheckRoutingIncomeDetail } from '../../types/PaycheckRouting';
import { paycheckMatchesRoutingConfig } from '../paycheck-routing/helpers';
import NeedMoreAccountsForPaycheckRoutingSheet from '../paycheck-routing/NeedMoreAccountsForPaycheckRoutingSheet';

interface SpaceCardProps {
    bankAccountDetail:
        | BankAccountDisplayDetail
        | Partial<BankAccountDisplayDetail>;
    isJointMembership: boolean;
    onPress?: () => void;
}

const SpaceCard = ({
    bankAccountDetail,
    isJointMembership,
    onPress,
}: SpaceCardProps) => {
    const [isNeedMoreAccountsSheetOpen, setIsNeedMoreAccountsSheetOpen] =
        useState(false);

    const { availableToWithdraw, jointUserRelationshipStatus, icon, color } =
        bankAccountDetail;

    const isJointAccount =
        jointUserRelationshipStatus === RelationshipStatus.ACTIVE;

    const existingPaycheckRoutingConfigs = useAtomValue(
        existingPaycheckRoutingConfigsAtom,
    );
    const paychecks: PaycheckRoutingIncomeDetail[] = useAtomValue(
        detectedPaychecksAtom,
    );

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    const hasEligiblePaycheck = paychecks.find((paycheck) => {
        const paycheckHasActivePaycheckRoutingConfig =
            existingPaycheckRoutingConfigs.find(
                (paycheckRoutingConfig) =>
                    paycheckRoutingConfig.isActive &&
                    paycheckMatchesRoutingConfig(
                        paycheck,
                        paycheckRoutingConfig,
                    ),
            );
        return (
            paycheck.accountId === bankAccountDetail.bankAccountId &&
            !paycheckHasActivePaycheckRoutingConfig
        );
    });

    const paycheckForSpace = paychecks.find(
        (paycheck) => paycheck.accountId === bankAccountDetail.bankAccountId,
    );
    const accounts = useAtomValue(bankAccountDisplayDetails);

    const hasAtLeastTwoFruitfulCashAccounts = accounts.length > 1;

    const accountName = getDefaultedAccountNickname(bankAccountDetail);
    const balance = availableToWithdraw ?? '$0.00';

    return (
        <YStack alignItems="center" width="100%">
            <AccountRow
                accountName={accountName}
                accountIcon={icon}
                accountColor={color}
                isJointAccount={isJointAccount}
                isJointMembership={isJointMembership}
                balance={balance}
                onPress={onPress}
            />
            {paycheckForSpace &&
                !shouldSeeIncomeSplit &&
                hasEligiblePaycheck && (
                    <XStack
                        backgroundColor="$wasabi200"
                        paddingTop={22}
                        paddingBottom={14}
                        marginTop={-8}
                        paddingHorizontal={14}
                        width="95%"
                        justifyContent="space-between"
                        alignItems="center"
                        borderRadius={12}
                        onPress={() => {
                            if (existingPaycheckRoutingConfigs.length) {
                                router.navigate(
                                    '/banking/paycheck-routing/account-detection',
                                );
                            } else if (hasAtLeastTwoFruitfulCashAccounts) {
                                router.navigate(
                                    '/banking/paycheck-routing/info',
                                );
                            } else {
                                // show the sheets
                                setIsNeedMoreAccountsSheetOpen(true);
                            }
                        }}
                    >
                        <XStack gap={8} alignItems="center">
                            <Icon
                                icon={DownloadIcon}
                                size={14}
                                color="$black"
                            />

                            <BodySmallRegular>
                                We found a paycheck! Set up auto-split
                            </BodySmallRegular>
                        </XStack>
                        <Icon icon={ChevronRightIcon} color="$black" />
                    </XStack>
                )}
            <NeedMoreAccountsForPaycheckRoutingSheet
                isOpen={isNeedMoreAccountsSheetOpen}
                onClose={() => {
                    setIsNeedMoreAccountsSheetOpen(false);
                }}
                onSubmit={() => {
                    setIsNeedMoreAccountsSheetOpen(false);
                    router.navigate('/banking/onboarding/space-ownership');
                }}
            />
        </YStack>
    );
};

export default SpaceCard;
